import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@components/ui/dialog";
import { Button } from "@components/ui/button";

interface ReleaseDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  domain: string;
  /** Platform subdomains return a credit on release; custom domains don't. */
  isPlatformSubdomain: boolean;
  isLoading?: boolean;
}

/**
 * Confirmation for releasing a domain claim. Releasing is a hard giveback — the
 * hostname returns to the pool and (for platform subdomains) the credit is
 * refunded. If the domain is currently assigned to the site, the site will have
 * no domain until another is chosen.
 */
export function ReleaseDomainModal({
  isOpen,
  onClose,
  onConfirm,
  domain,
  isPlatformSubdomain,
  isLoading = false,
}: ReleaseDomainModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="release-domain-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center size-10 rounded-full bg-amber-100">
              <ExclamationTriangleIcon className="size-5 text-amber-600" />
            </div>
            <DialogTitle>Release this domain?</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            You're about to release <span className="font-semibold text-base-600">{domain}</span>.
            It returns to the pool and anyone can claim it again.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-base-500">
            {isPlatformSubdomain
              ? "You'll get this Launch10 subdomain credit back, so you can claim a different one."
              : "You can reconnect this domain later, but you'll need to re-verify it."}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={isLoading} data-testid="cancel-release-button">
            Keep it
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="confirm-release-button"
          >
            {isLoading ? "Releasing…" : "Release domain"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
